import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

const STORAGE_KEY = 'hudstore-recent-pages'
const MAX_RECENT = 5

interface RecentPage {
  path: string
  label: string
  icon: string
  timestamp: number
}

// Global state
const isOpen = ref(false)
const searchQuery = ref('')
const recentPages = ref<RecentPage[]>(loadRecentPages())

function loadRecentPages(): RecentPage[] {
  if (typeof window === 'undefined') return []
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved ? JSON.parse(saved) : []
  } catch {
    return []
  }
}

function saveRecentPages(pages: RecentPage[]) {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pages))
  }
}

// Auto-save recent pages
watch(recentPages, (pages) => saveRecentPages(pages), { deep: true })

export function useCommandPalette() {
  const router = useRouter()

  const open = () => {
    isOpen.value = true
    searchQuery.value = ''
  }

  const close = () => {
    isOpen.value = false
    searchQuery.value = ''
  }

  const toggle = () => {
    if (isOpen.value) {
      close()
    } else {
      open()
    }
  }

  const addToRecent = (page: Omit<RecentPage, 'timestamp'>) => {
    // Remove if already exists
    const filtered = recentPages.value.filter((p) => p.path !== page.path)

    // Add to front with timestamp
    recentPages.value = [
      { ...page, timestamp: Date.now() },
      ...filtered,
    ].slice(0, MAX_RECENT)
  }

  const clearRecent = () => {
    recentPages.value = []
  }

  const navigateTo = (path: string, label: string, icon: string) => {
    addToRecent({ path, label, icon })
    router.push(path)
    close()
  }

  // Keyboard shortcut handler
  const handleKeydown = (e: KeyboardEvent) => {
    // Cmd+K or Ctrl+K to open
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault()
      toggle()
    }

    // Escape to close
    if (e.key === 'Escape' && isOpen.value) {
      e.preventDefault()
      close()
    }
  }

  // Register global keyboard shortcut
  onMounted(() => {
    window.addEventListener('keydown', handleKeydown)
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown)
  })

  return {
    // State
    isOpen: computed(() => isOpen.value),
    searchQuery,
    recentPages: computed(() => recentPages.value),

    // Actions
    open,
    close,
    toggle,
    addToRecent,
    clearRecent,
    navigateTo,
  }
}

// Standalone function to register keyboard shortcut (for App.vue)
export function registerCommandPaletteShortcut() {
  const handleKeydown = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault()
      isOpen.value = !isOpen.value
      if (!isOpen.value) {
        searchQuery.value = ''
      }
    }

    if (e.key === 'Escape' && isOpen.value) {
      e.preventDefault()
      isOpen.value = false
      searchQuery.value = ''
    }
  }

  window.addEventListener('keydown', handleKeydown)

  return () => {
    window.removeEventListener('keydown', handleKeydown)
  }
}
